import React from "react";

import { useAppState } from "Context/StateContext";
import { ResourceSubGroup } from "Util/enums";
import { operationsPermissions } from "Util/permissions";

import { Checklist } from "../../../../../models/checklist";
import { Report } from "../../../../../models/report";
import { ReportGroup } from "../../../../../models/reports.groups";
import ReportListItem from "../ReportListItem";
import useStyles from "./styles";
import { RolePermissionsResponse } from "Models/resources";
import { useTranslation } from "react-i18next";

interface Props {
  group: Group;
  searchValue?: string;
  addToFavorites?: (reportId?: number, checklistId?: number) => void;
  removeFromFavorites?: (reportId?: number, checklistId?: number) => void;
  checklists?: Checklist[];
  favoriteReports: Report[];
  favoriteChecklists: Checklist[];
  reportsToView?: RolePermissionsResponse[]
}

interface Group {
  reportGroupName: string;
  reportGroupItems: ReportGroup[];
}

const ReportItem = ({
  group,
  searchValue,
  addToFavorites,
  removeFromFavorites,
  checklists,
  favoriteReports,
  favoriteChecklists,
  reportsToView
}: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const classes = useStyles();

  const orderGroupItems = (reportGroup: ReportGroup[]) => {
    return reportGroup.sort((reportGroupItemA, reportGroupItemB) => {
      if (
        reportGroupItemA?.subGroup &&
        reportGroupItemB?.subGroup &&
        reportGroupItemA?.subGroup < reportGroupItemB?.subGroup
      ) {
        return -1;
      }
      if (
        reportGroupItemA?.subGroup &&
        reportGroupItemB?.subGroup &&
        reportGroupItemA?.subGroup > reportGroupItemB?.subGroup
      ) {
        return 1;
      }

      return 0;
    });
  };

  const getPermissionToViewReports = (group: string, subGroup?: string) => {
    switch (group.toLowerCase()) {
      case "operations": {
        const hasOperationsPermission = operationsPermissions({
          subGroup: ResourceSubGroup.OPERATIONS,
          resourceName: subGroup,
          currentUser,
        });

        return hasOperationsPermission?.view;
      }

      default:
        return true;
    }
  };

  const renderReportGroupItems = () => {
    if (group && group.reportGroupItems && group.reportGroupItems.length > 0) {
      return orderGroupItems(group.reportGroupItems).map((reportGroup) => {
        const hasPermissionToView = getPermissionToViewReports(
          group.reportGroupName,
          reportGroup.subGroup
        );

        if (!hasPermissionToView) return null;

        return (
          <ReportListItem
            addToFavorites={addToFavorites}
            removeFromFavorites={removeFromFavorites}
            searchValue={searchValue}
            key={reportGroup.id}
            reportGroup={reportGroup}
            checklists={checklists}
            favoriteReports={favoriteReports}
            favoriteChecklists={favoriteChecklists}
            reportsToView={reportsToView}
          />
        );
      });
    }

    return null;
  };

  return (
    <>
      <span className={classes.reportSubheader}>{translate(group.reportGroupName?.toLowerCase())}</span>
      {renderReportGroupItems()}
    </>
  );
};

export default ReportItem;
